import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ALLOWED_INTERCOM_COUNTRIES } from '../constants/intercom';
import type { RootState } from '../store/store';
import { SubscriptionAccess } from '../types/Subscription';
import { runAfterIdle } from '../utils/idle';

const ALLOWED_COUNTRIES_SET = new Set(ALLOWED_INTERCOM_COUNTRIES);
let countryPromise: Promise<string | null> | null = null;

const fetchCountryFromApi = async (): Promise<string | null> => {
  if (!countryPromise) {
    countryPromise = fetch('/api/country')
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json();
        if (data && typeof data.country === 'string') {
          return data.country.toUpperCase();
        }
        return null;
      })
      .catch(() => null);
  }
  return countryPromise;
};

/**
 * Determines whether Intercom should render.
 * If the user is subscribed, always show.
 * Otherwise, only show for allowed countries; fall back to showing in development
 * when the country cannot be resolved (to match marketing-site behavior).
 */
export const useIntercomVisibility = (isSubscribed: boolean) => {
  const [countryCode, setCountryCode] = useState<string | null>(null);

  useEffect(() => {
    if (isSubscribed) return;
    let isCancelled = false;

    const cancelIdle = runAfterIdle(() => {
      fetchCountryFromApi().then((code) => {
        if (isCancelled) return;
        setCountryCode(code);
      });
    });

    return () => {
      isCancelled = true;
      cancelIdle();
    };
  }, [isSubscribed]);

  return useMemo(() => {
    if (isSubscribed) return true;

    if (countryCode) {
      return ALLOWED_COUNTRIES_SET.has(countryCode);
    }

    // Unknown country: only allow in development, mirroring marketing-site logic.
    return import.meta.env.DEV;
  }, [countryCode, isSubscribed]);
};

/**
 * Whether a human is reachable for this student at all — a paid account, or a
 * country support covers. Both the legacy support button and the companion
 * shell's "Human →" ask this, so neither offers a channel that would open on
 * nothing.
 */
export const useCanShowIntercom = (): boolean => {
  const accessLevel = useSelector(
    (state: RootState) => state.subscription.subscription?.accessLevel
  );

  const isSubscribed =
    accessLevel !== undefined &&
    accessLevel !== SubscriptionAccess.FREE &&
    accessLevel !== SubscriptionAccess.TRIAL;

  return useIntercomVisibility(isSubscribed);
};
